import React, { type ComponentProps, forwardRef } from 'react';
import { type ViewProps } from 'react-native';
import Animated from 'react-native-reanimated';

import { FocusScope } from '../FocusScope';
import { useAnimatedDialogAnimation } from './hooks';

export const AnimatedDialogView = forwardRef<
  Animated.View,
  AnimatedDialogContentProps
>((props, ref) => {
  const { children, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show: true });

  return (
    <FocusScope>
      <Animated.View {...rest} ref={ref} style={[animation, style]}>
        {children}
      </Animated.View>
    </FocusScope>
  );
});

export const AnimatedDialogScrollView = forwardRef<
  Animated.ScrollView,
  AnimatedDialogContentProps
>((props, ref) => {
  const { children, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show: true });

  return (
    <FocusScope>
      <Animated.ScrollView {...rest} ref={ref} style={[animation, style]}>
        {children}
      </Animated.ScrollView>
    </FocusScope>
  );
});

export const AnimatedDialogScrollViewMounted = forwardRef<
  Animated.ScrollView,
  AnimatedDialogScrollViewMountedProps
>((props, ref) => {
  const { children, show, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show });

  return (
    <FocusScope enabled={show}>
      <Animated.ScrollView
        {...rest}
        ref={ref}
        pointerEvents={show ? 'auto' : 'none'}
        style={[animation, style]}
      >
        {children}
      </Animated.ScrollView>
    </FocusScope>
  );
});

// ─── Types ──────────────────────────────────────────────────────────────────

type AnimatedDialogContentProps = ViewProps &
  Omit<
    ComponentProps<typeof Animated.ScrollView | typeof Animated.View>,
    'ref'
  >;

type AnimatedDialogScrollViewMountedProps = AnimatedDialogContentProps & {
  show: boolean;
};
